import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SafeUrl } from '@angular/platform-browser';
import { Logger } from 'aws-amplify';
import { Observable, from, of, timer } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Clinic, NameFormat } from 'src/API';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { FormTemplateGeneratorService } from 'src/app/form-template/form-template-generator/form-template-generator.service';
import { FormTemplateInputFormGroup } from 'src/app/form-template/form-template-input-form-group';
import { FormTemplateSchemaType } from 'src/app/form-template/form-template-model';
import { S3AttachmentService } from 'src/app/shared/attachments/s3-attachment.service';
import { defaultLogoUri } from 'src/app/shared/dry-eye-pdf-export/default-logo-uri.model';

const componentStyle = require('./questionnaire-pdf-export.component.css');

/** Prefer these form date fields (in order) for the consent PDF document header. */
const CONSENT_PDF_HEADER_DATE_KEYS = [
  'consentDate',
  'treatmentDate',
  'followUpDate',
  'patientSignDate'
];

@Component({
  selector: 'csi-questionnaire-pdf-export',
  templateUrl: './questionnaire-pdf-export.component.html',
  styleUrls: ['./questionnaire-pdf-export.component.css']
})
export class QuestionnairePdfExportComponent implements OnInit {
  @Input() data;
  @Input() clinic: Clinic;
  @Input() clinicLogo;

  public clinicLogoDataUri: SafeUrl;
  public defaultLogo = defaultLogoUri;
  public isConsentForm = false;
  /** Consent PDF title date, e.g. July 10, 2026 */
  public consentPdfHeaderDate = '';
  /** Consent form title (schema name), e.g. IPL Treatment Consent */
  public consentPdfFormTitle = '';
  /** Combined string for Puppeteer page header meta tag */
  public consentPdfPageHeader = '';

  private logger: Logger = new Logger('PDFExportService');
  public formGroup: FormTemplateInputFormGroup;
  public medicationControl: FormControl = new FormControl();
  public readonly NameFormat = NameFormat;

  constructor(
    private s3AttachmentService: S3AttachmentService,
    private hostElementRef: ElementRef,
    private changeDetector: ChangeDetectorRef,
    private formTemplateGeneratorService: FormTemplateGeneratorService,
    public questionnaireRequestService: QuestionnaireRequestService
  ) {}

  ngOnInit() {
    const medications = this.data.questionnaireResponseData.medications;

    if (medications) {
      this.medicationControl.setValue(medications);
      this.medicationControl.disable();
    }
    if (!this.data.questionnaireResponseData.schema) return;

    const schema = this.data.questionnaireResponseData.schema;
    this.isConsentForm = schema.type === FormTemplateSchemaType.consent;

    this.formGroup = new FormTemplateInputFormGroup(schema);
    const formValues =
      this.data.questionnaireResponseData.formGroupWithValues &&
      this.data.questionnaireResponseData.formGroupWithValues.getRawValue
        ? this.data.questionnaireResponseData.formGroupWithValues.getRawValue()
        : this.data.questionnaireResponseData.formGroupWithValues.value;
    this.formGroup.patchValue(formValues);

    if (this.isConsentForm) {
      this.consentPdfHeaderDate = this.formatConsentPdfHeaderDate(
        formValues,
        this.data.questionnaireResponseData.completedAt
      );
      this.consentPdfFormTitle = schema.name || schema.abbreviation || '';
      this.consentPdfPageHeader = [this.consentPdfHeaderDate, this.consentPdfFormTitle]
        .filter(part => !!part)
        .join(' — ');
    }
  }

  private formatConsentPdfHeaderDate(formValues: any, completedAt?: string): string {
    const datePipe = new DatePipe('en-US');
    const flat = this.flattenFormValues(formValues);
    let raw: any = null;
    for (let i = 0; i < CONSENT_PDF_HEADER_DATE_KEYS.length; i++) {
      const key = CONSENT_PDF_HEADER_DATE_KEYS[i];
      if (flat[key]) {
        raw = flat[key];
        break;
      }
    }
    if (!raw && completedAt) {
      raw = completedAt;
    }
    if (!raw) {
      return '';
    }
    return datePipe.transform(raw, 'MMMM d, y') || '';
  }

  private flattenFormValues(obj: any, out: { [key: string]: any } = {}): { [key: string]: any } {
    if (!obj || typeof obj !== 'object' || obj instanceof Date || Array.isArray(obj)) {
      return out;
    }
    Object.keys(obj).forEach(key => {
      const val = obj[key];
      if (val && typeof val === 'object' && !(val instanceof Date) && !Array.isArray(val)) {
        this.flattenFormValues(val, out);
      } else {
        out[key] = val;
      }
    });
    return out;
  }

  get html(): Observable<string> {
    this.logger.debug('rendering pdf html');
    // Clear before first CD so signature fields can register rasterize promises.
    this.formTemplateGeneratorService.resetPdfRasterizePromises();
    return this.render().pipe(
      tap(() => this.changeDetector.detectChanges()),
      // Wait until after CD so isConsentForm is set; await Fabric rasterize if any.
      switchMap(() =>
        from(this.formTemplateGeneratorService.waitForPdfRasterizePromises()).pipe(
          switchMap(() => timer(this.isConsentForm ? 50 : 0))
        )
      ),
      tap(() => this.changeDetector.detectChanges()),
      map(() => {
        this.logger.debug('calling innerHTMLWithStyle');
        return this.innerHTMLWithStyle;
      })
    );
  }

  private render(): Observable<boolean> {
    this.logger.debug('render() called');
    return this.clinicLogoDataUri ? of(true) : this.renderClinicLogo();
  }
  private renderClinicLogo(): Observable<boolean> {
    this.logger.debug('rendering clinic logo');
    return of(this.clinicLogo).pipe(
      switchMap(logo => (logo ? this.s3AttachmentService.s3ObjectToUri(logo) : of(null))),
      map(logoDataUri => {
        this.logger.debug('setting logo data uri');
        this.clinicLogoDataUri = logoDataUri;
        return true;
      })
    );
  }

  private get innerHTMLWithStyle(): string {
    this.logger.debug('setting inner html');
    return this.hostElementRef.nativeElement.innerHTML.replace(
      '</head>',
      `<style>${componentStyle.default}</style></head>`
    );
  }
  noSort() {
    return 0;
  }
}
